/**
 * One place for every "what does this button do?" explanation.
 * Plain English, benefit-first, no jargon — parents read these, not engineers.
 * Keep them to 1-3 sentences so the tooltip stays readable on a phone.
 */
export const HELP = {
  exams: {
    addExam:
      "Create an exam from scratch — name it, set the dates, pick the subjects. Everything the app plans for your child hangs off an exam, so this is the starting point.",
    tabs:
      "Active shows exams still ahead or running. Archived keeps finished ones for your records — nothing is ever deleted.",
    studentSelect:
      "You have more than one child. Pick whose exams you're looking at — each child has their own list, portion and plan.",
    shareCodeInput:
      "Another parent already built this exam. They send you their code, you paste it here and press Adopt — you get your own copy of their whole exam: dates, subjects and chapter list. One organised parent sets it up, thirty others get it in a tap. You'll see exactly what's about to be copied before anything lands in your list, and your copy is independent of theirs.",
    adoptBtn:
      "Checks the code and shows you a review card first. Nothing is copied into your list until you confirm.",
    myShareCode:
      "This exam's own code. Copy it and send it to another parent — they can adopt this exam and its chapters in one tap. Their edits never touch your exam.",
    editMap:
      "Change the name, dates and subjects, and tick chapters at a broad level.",
    mapPortion:
      "Go finer than chapters. Open the approved syllabus as a tree, pick exact topics and concepts, mark anything only partly included, and see whether there's realistically enough time left before the exam.",
    differences:
      "Your child's portion isn't always the class portion. Drop a chapter the teacher excused, or add extra reading. The class portion stays untouched and you can revert any time.",
    duplicate:
      "Copy this exam with its whole portion, then just change the dates. Handy for the same test next term.",
    archive:
      "Move it out of your active list without losing it. You can restore it any time.",
    restore:
      "Bring this exam back into your active list, portion and all.",
    mappedPct:
      "How much of the portion you've actually mapped. The plan can only schedule what's been mapped, so aim for 100%.",
    countdown:
      "Days until the exam starts. The study plan gets tighter as this shrinks.",
    import:
      "Nobody to copy from — you just have the school's circular. Paste it one subject per line and we match each chapter against your child's approved syllabus, then show green (recognised) and amber (couldn't place, and why). We'll never invent a chapter that isn't in the syllabus — if the school's wording doesn't match, we ask you rather than guessing.",
    reviewProposals:
      "Reads your pasted text and shows what it found. This creates nothing — it's a preview you can throw away.",
    confirmImport:
      "Creates the exam from the green matches only. Amber lines are skipped — you can add those by hand afterwards.",
    discardImport:
      "Throws away this reading of the notice. Nothing was created, so nothing is lost.",
    adoptPreview:
      "This is a preview, not your exam yet. Check the dates and chapters, then confirm to make your own copy.",
  },

  mapper: {
    subjectTabs:
      "One paper per subject. Switch here to map each subject's portion separately.",
    tree:
      "Your school's approved syllabus. Tick a chapter and everything inside it is ticked too, or open it up and pick single topics.",
    expand:
      "Open a chapter to see the topics inside it, so you can pick only the parts that are actually in the exam.",
    addToExam:
      "Moves everything you've ticked onto the exam paper on the right. Nothing is saved until you press Save portion.",
    partial:
      "Tap to switch between the whole thing and only part of it. Partial counts as roughly half the study time, so the plan stays realistic.",
    notes:
      "Tell the plan what 'partial' means here — 'diagrams only', 'skip the derivation'. Your child sees this when they study it.",
    reorder:
      "Change the study order — the plan follows this sequence. Drag a row, or use these arrows if you'd rather not drag.",
    removeItem:
      "Takes this off the exam paper. It stays in the syllabus, so you can add it back any time.",
    save:
      "Saves the portion and re-checks whether it fits in the days left.",
    feasibility:
      "A reality check, not a blocker. We compare the study time this portion needs against the days left, keeping a fifth of the time aside for revision. If it doesn't fit we tell you — you decide what to do.",
    noSyllabusFile:
      "We can only break down chapters whose syllabus file has been attached. Ask your admin to upload it, then this chapter opens up into topics.",
  },

  overrides: {
    dropChip:
      "Tap a chapter to drop it for this child only. Tap again to put it back. The class portion never changes.",
    addExtra:
      "Add a chapter that isn't in the class portion — extra reading, or catching up on something missed.",
    notes:
      "A note for your own memory, and for the study plan — 'was absent', 'covering this at tuition'.",
    effective:
      "What your child will actually study: the class portion, minus what you dropped, plus what you added.",
    save: "Saves these differences for this child only.",
    revert:
      "Wipes your changes for this subject and goes back to exactly the class portion.",
  },

  study: {
    newChat:
      "Starts a fresh conversation. Your old chats stay in the list below, so nothing is lost.",
    changeMode:
      "Switch how the tutor behaves — Explore to ask anything, Exam Mode for a guided run at today's portion. Your choice is remembered until you change it here.",
    collapse:
      "Fold the sidebar away to give the conversation more room. Your chats stay one tap away.",
    recentChat:
      "Pick up an earlier conversation exactly where you left it — the tutor still remembers the context.",
    modeExplore:
      "Ask anything, in any order. Best when your child has a specific doubt right now and just wants an answer they understand.",
    modeStudyBuddy:
      "The tutor suggests what to do next — a video, a quiz, flashcards — instead of waiting to be asked. Best when they know they should study but not what to start with.",
    modeAiTutor:
      "A full guided session: the tutor explains, checks understanding with a quiz, then reflects on how it went. Best when a chapter needs covering properly rather than patching one doubt.",
    modeLocked:
      "This mode is part of a paid plan. Tap to see what's included before you decide.",
    nextActions:
      "Suggestions based on what your child just studied. Tap one and the tutor takes them straight into it.",
    guidedProgress:
      "How far through today's session they are. Each step has to be genuinely finished — it can't be skipped to the end.",
    guidedDone:
      "Marks this step finished and unlocks the next one. Some steps are checked first, so it won't tick if the work isn't actually done.",
    guidedOpen:
      "Takes them to the quiz or flashcards for this step, then brings them back here to carry on.",
    guidedMastery:
      "The quiz score for this chapter. Below 80% and the tutor quietly adds a revision step rather than moving on.",
    voiceBrowser:
      "Talk to the tutor out loud instead of typing, in English. Good for reading practice, or when typing is slow.",
    voicePremium:
      "Voice tutoring in your regional language. Part of a paid plan, with a monthly minutes allowance.",
    send:
      "Sends your question. You can also just press Enter — Shift+Enter adds a new line.",
    simpleMode:
      "Turns on much simpler wording. Handy when an explanation went over their head.",
    newChatBtn:
      "Clears the screen for a new question. The chat you were in stays saved in the sidebar.",
    topicChip:
      "A chapter from your syllabus. Tap it and the tutor starts on that chapter straight away.",
    eli5:
      "Explain Like I'm 5 — the tutor answers in much simpler words. Handy when an explanation went over their head.",
    snapSolve:
      "Stuck on a printed question? Photograph it and the tutor reads the problem and works through it.",
    summary:
      "Condenses this whole conversation into a short recap they can revise from later.",
  },

  subscription: {
    managePlan:
      "See what you're currently paying for, when the next charge lands, and cancel or restart any plan.",
    trialBanner:
      "You're inside the free period — everything is unlocked and no card is needed. We'll tell you before anything changes.",
    monthly:
      "Pay month to month. Cheapest way to start and you can stop any time.",
    yearly:
      "Pay for a year in one go and it works out cheaper per month than paying monthly.",
    lifetime:
      "One payment, no renewals ever. Best value if you're staying for the long run.",
    comingSoon:
      "This plan isn't open yet. Nothing is charged — we'll let you know the moment it launches.",
    currentPlan:
      "This is the plan you're on right now.",
    upgradeLoyalty:
      "Already paying? Upgrades come with a loyalty bonus — your first stretch is free and the next charge shifts back accordingly.",
    cancel:
      "Stops future charges. Your access continues until the period you've already paid for runs out — you don't lose anything you've paid for.",
    resubscribe:
      "Start this plan up again. Your history and progress are all still here.",
    firstCharge:
      "The date your first real payment happens. Anything before that is free.",
    halted:
      "A payment didn't go through. We keep access live for a short grace period while you fix your card, rather than cutting you off immediately.",
    cancelAtPeriodEnd:
      "Already cancelled, but still paid up — access runs to the end of the current period, then stops.",
  },

  // One-time Study Buddy greetings, shown once per screen per user.
  tips: {
    exams:
      "New here? Every study plan hangs off an exam. Hover any button and I'll tell you what it does \u2014 or tap the little ⓘ circles.",
    mapper:
      "This is where you pick exactly what's in the exam. Tick a chapter to grab everything inside it, or open it up and choose single topics. I'll warn you if there isn't enough time left.",
    chat:
      "Tip: tap the mode button on the left to change how I help \u2014 answering doubts, suggesting what's next, or walking you through a whole chapter.",
    subscription:
      "Take your time comparing. Hover any button to see exactly what it charges and when \u2014 nothing happens until you confirm on the payment screen.",
    // {occasion} / {label} / {days} are filled in at call time.
    seasonalTheme:
      "Something new! The {label} theme is here just for {occasion} — only {days} days before it goes away. Open Profile → Study theme to try it.",
    manageSubscription:
      "Everything you're paying for is listed here. Cancelling never takes away time you've already paid for.",
  },
};

export default HELP;
